<template>
  <div
    ref="containerRef"
    class="audio-player"
    :class="[
      {
        'audio-player--fullscreen': player.isFullscreen.value,
        'audio-player--loading': player.isLoading.value,
        'audio-player--error': player.error.value,
        'audio-player--playing': player.isPlaying.value,
      },
      themeClass,
    ]"
    :style="dynamicStyles"
    tabindex="0"
    @keydown="handleKeyDown"
  >
    <!-- Background blur (cover art) — only for colorful theme -->
    <div v-if="theme === 'colorful'" class="audio-player__bg" aria-hidden="true">
      <img
        v-if="coverArtUrl"
        :src="coverArtUrl"
        alt=""
        class="audio-player__bg-image"
      />
      <div class="audio-player__bg-overlay" />
    </div>

    <!-- Main content -->
    <div class="audio-player__content">
      <!-- Cover art section -->
      <div class="audio-player__cover">
        <Transition name="audio-cover-fade" mode="out-in">
          <img
            v-if="coverArtUrl"
            :key="coverArtUrl"
            :src="coverArtUrl"
            :alt="currentTitle"
            class="audio-player__cover-image"
          />
          <div v-else :key="'placeholder'" class="audio-player__cover-placeholder">
            <v-icon size="48" color="grey-darken-1" :icon="mdiMusic" />
          </div>
        </Transition>

        <!-- Play overlay on cover (visible when paused) -->
        <button
          v-if="!player.isPlaying.value && !player.isLoading.value"
          class="audio-player__cover-play"
          :aria-label="t('player.controls.play')"
          @click="player.togglePlay()"
        >
          <v-icon size="32" color="white" :icon="mdiPlay" />
        </button>
      </div>

      <!-- Main section: info + waveform -->
      <div class="audio-player__main">
        <!-- Track info -->
        <div class="audio-player__info">
          <Transition name="audio-title-fade" mode="out-in">
            <h3 :key="currentTitle" class="audio-player__title">{{ currentTitle }}</h3>
          </Transition>
          <span v-if="config.showChapters !== false && currentChapterTitle" class="audio-player__chapter-label">
            {{ currentChapterTitle }}
          </span>
        </div>

        <!-- Waveform or progress bar fallback -->
        <div class="audio-player__waveform">
          <AudioWaveform
            v-if="config.showWaveform !== false"
            :waveform-data="player.waveform.value"
            :current-time="player.currentTime.value"
            :duration="player.duration.value"
            :buffered="player.buffered.value"
            :interactive="player.canSeek.value"
            :chapters="config.showChapters !== false ? chapters : []"
            :played-color="accentColor"
            :theme="theme"
            @seek="handleSeek"
          />
          <AudioProgressBar
            v-else
            :current-time="player.currentTime.value"
            :duration="player.duration.value"
            :buffered="player.buffered.value"
            :accent-color="accentColor"
            :interactive="player.canSeek.value"
            @seek="handleSeek"
          />
        </div>

        <!-- Subtitle cue text (fixed height container to avoid layout shifts) -->
        <div
          v-if="hasSubtitles && subtitlesEnabled"
          class="audio-player__subtitle"
        >
          <Transition name="audio-subtitle-fade">
            <span
              v-if="subtitlesComposable.activeCueText.value"
              :key="subtitlesComposable.activeCueText.value"
              class="audio-player__subtitle-text"
            >
              {{ subtitlesComposable.activeCueText.value }}
            </span>
          </Transition>
        </div>

        <!-- Error state -->
        <div v-if="player.error.value" class="audio-player__error">
          <v-icon color="error" size="24" :icon="mdiAlertCircle" />
          <span class="audio-player__error-text">{{ player.error.value.message }}</span>
          <v-btn variant="tonal" color="primary" size="small" @click="handleRetry">
            {{ t('common.retry') }}
          </v-btn>
        </div>
      </div>
    </div>

    <!-- Toolbar -->
    <AudioToolbar
      :is-playing="player.isPlaying.value"
      :is-loading="player.isLoading.value"
      :current-time="player.currentTime.value"
      :duration="player.duration.value"
      :volume="player.volume.value"
      :is-muted="player.isMuted.value"
      :playback-speed="player.playbackSpeed.value"
      :is-loop="player.isLoop.value"
      :is-shuffle="player.isShuffle.value"
      :is-auto-play-next="player.isAutoPlayNext.value"
      :is-fullscreen="player.isFullscreen.value"
      :can-prev="player.canGoPrev.value"
      :can-next="player.canGoNext.value"
      :show-prev-next="player.totalItems.value > 1"
      :show-volume="config.enableVolume !== false"
      :show-playback-speed="config.enablePlaybackSpeed !== false"
      :show-loop="config.enableLoop !== false"
      :show-shuffle="config.enableShuffle !== false && player.totalItems.value > 1"
      :show-auto-play-next="config.enableAutoPlayNext !== false && player.totalItems.value > 1"
      :show-fullscreen="config.enableFullscreen !== false"
      :subtitle-tracks="subtitleTracks"
      :subtitles-enabled="subtitlesEnabled"
      :current-subtitle-track-index="currentSubtitleTrackIndex"
      @toggle-play="handleTogglePlay"
      @prev="handlePrev"
      @next="handleNext"
      @toggle-mute="handleToggleMute"
      @volume-change="handleVolumeChange"
      @speed-change="handleSpeedChange"
      @toggle-loop="player.toggleLoop"
      @toggle-shuffle="player.toggleShuffle"
      @toggle-auto-play-next="player.toggleAutoPlayNext"
      @toggle-fullscreen="handleToggleFullscreen"
      @toggle-subtitles="handleToggleSubtitles"
      @subtitle-track-change="handleSubtitleTrackChange"
    />

    <!-- Chapters list (optional) -->
    <div v-if="config.showChapters !== false && chaptersComposable.hasChapters.value" class="audio-player__chapters">
      <button
        class="audio-player__chapters-header"
        :aria-expanded="!isChaptersCollapsed"
        @click="isChaptersCollapsed = !isChaptersCollapsed"
      >
        <span class="audio-player__chapters-toggle">
          <v-icon
            size="18"
            :icon="isChaptersCollapsed ? mdiChevronDown : mdiChevronUp"
          />
        </span>
        <span class="audio-player__chapters-title">{{ t('player.sections.chapters') }}</span>
        <span class="audio-player__chapters-count">
          {{ chaptersComposable.currentChapterIndex.value + 1 }} / {{ chapters.length }}
        </span>
      </button>
      <Transition name="audio-playlist-collapse">
        <ul
          v-show="!isChaptersCollapsed"
          class="audio-player__chapters-list"
        >
          <li
            v-for="(chapter, index) in chapters"
            :key="index"
            class="audio-player__chapters-item"
            :class="{
              'audio-player__chapters-item--active': index === chaptersComposable.currentChapterIndex.value,
              'audio-player__chapters-item--still': !player.canSeek.value,
            }"
            @click="handleSeek(chapter.startTime)"
          >
            <span class="audio-player__chapters-time">{{ formatTime(chapter.startTime) }}</span>
            <span class="audio-player__chapters-name">{{ chapter.title }}</span>
          </li>
        </ul>
      </Transition>
    </div>

    <!-- Playlist (optional) -->
    <div v-if="showPlaylist && player.totalItems.value > 1" class="audio-player__playlist">
      <button
        class="audio-player__playlist-header"
        :aria-expanded="!isPlaylistCollapsed"
        @click="isPlaylistCollapsed = !isPlaylistCollapsed"
      >
        <span class="audio-player__playlist-toggle">
          <v-icon
            size="18"
            :icon="isPlaylistCollapsed ? mdiChevronDown : mdiChevronUp"
          />
        </span>
        <span class="audio-player__playlist-title">{{ t('player.sections.playlist') }}</span>
        <span class="audio-player__playlist-count">
          {{ player.currentIndex.value + 1 }} / {{ player.totalItems.value }}
        </span>
      </button>
      <Transition name="audio-playlist-collapse">
        <div
          v-show="!isPlaylistCollapsed"
          ref="playlistItemsRef"
          class="audio-player__playlist-items"
        >
          <AudioPlaylistItem
            v-for="(item, index) in player.items.value"
            :key="item.asset.uid"
            :ref="(el) => setPlaylistItemRef(index, el as any)"
            :index="index + 1"
            :title="item.customTitle || item.asset.name"
            :description="item.customDescription || item.asset.description"
            :thumbnail-url="item.asset.thumbnailUrl"
            :duration="item.asset.duration"
            :is-active="index === player.currentIndex.value"
            :is-playing="index === player.currentIndex.value && player.isPlaying.value"
            @select="player.goToIndex(index)"
          />
        </div>
      </Transition>
    </div>

    <!-- Downloads section (optional) -->
    <div v-if="hasDownloads" class="audio-player__downloads">
      <button
        class="audio-player__downloads-header"
        :aria-expanded="!isDownloadsCollapsed"
        @click="isDownloadsCollapsed = !isDownloadsCollapsed"
      >
        <span class="audio-player__downloads-toggle">
          <v-icon
            size="18"
            :icon="isDownloadsCollapsed ? mdiChevronDown : mdiChevronUp"
          />
        </span>
        <span class="audio-player__downloads-title">{{ t('player.sections.downloads') }}</span>
        <span class="audio-player__downloads-count">
          {{ downloadsCount }}
        </span>
      </button>
      <Transition name="audio-playlist-collapse">
        <div v-show="!isDownloadsCollapsed" class="audio-player__downloads-list">
          <!-- Source audio download -->
          <a
            v-if="sourceDownloadUrl"
            :href="sourceDownloadUrl"
            class="audio-player__downloads-item"
            :aria-label="t('player.sections.downloadSource')"
          >
            <v-icon size="18" :icon="mdiFileMusicOutline" />
            <span class="audio-player__downloads-name">{{ t('player.sections.sourceAudio') }}</span>
            <v-icon size="16" :icon="mdiDownload" class="audio-player__downloads-action" />
          </a>
          <!-- Linked files -->
          <a
            v-for="file in linkedFiles"
            :key="file.uid"
            :href="file.fileUrl?.url"
            class="audio-player__downloads-item"
            :class="{ 'audio-player__downloads-item--disabled': !file.fileUrl }"
            :aria-label="`${t('player.sections.download')} ${file.name}`"
            @click="!file.fileUrl && $event.preventDefault()"
          >
            <v-icon size="18" :icon="linkedFileIcon(file.type)" />
            <span class="audio-player__downloads-name">{{ file.name }}</span>
            <v-icon v-if="file.fileUrl" size="16" :icon="mdiDownload" class="audio-player__downloads-action" />
          </a>
        </div>
      </Transition>
    </div>

    <!-- Branding logo -->
    <div
      v-if="showBranding"
      class="audio-player__branding"
      aria-hidden="true"
    >
      <!-- Custom logo from settings -->
      <img
        v-if="brandingLogoUrl"
        :src="brandingLogoUrl"
        alt=""
        class="audio-player__branding-custom-logo"
      />
      <!-- Default Videas logo -->
      <svg v-else xmlns="http://www.w3.org/2000/svg" width="72" height="18" viewBox="0 0 920 226" class="audio-player__branding-logo">
        <g fill="currentColor">
          <path d="M7.35,65.27h36.27l37.65,115.82h.63l36.77-115.82h34.38l-53.96,150.58h-37.65Z" />
          <path d="M176.54,7.4h33.25v31.5h-33.25V7.4ZM176.54,64.88h33.25v150.58h-33.25V64.88Z" />
          <path d="M351.93,90.03c-11.85-13.74-29.16-21.55-47.31-21.33-19.4-.05-37.98,7.83-51.44,21.8-13.46,13.98-20.62,32.85-19.83,52.23-.68,19.33,6.48,38.11,19.86,52.07,13.38,13.97,31.83,21.93,51.17,22.09,18.48.16,36.01-8.16,47.56-22.59l30.24,19.7V5.96h-30.24v84.08ZM308.64,188.92c-12.08-.2-23.58-5.21-31.95-13.93-8.37-8.72-12.9-20.42-12.6-32.5,0-24.88,20.17-45.05,45.05-45.05s45.05,20.17,45.05,45.05c.38,12.23-4.24,24.08-12.78,32.84-8.54,8.76-20.28,13.66-32.52,13.59h-.25Z" />
          <path d="M471.8,63.54c-20.64-.21-40.49,7.94-55.02,22.6-14.54,14.65-22.53,34.57-22.15,55.2-.36,20.72,7.83,40.67,22.65,55.16s34.95,22.22,55.66,21.39c28.89,1.19,55.91-14.27,69.52-39.78l-26.23-14.56c-7.8,16.75-24.82,27.26-43.29,26.73-23.23,1.56-43.87-14.7-47.81-37.65h122.85v-12.55c.85-20.49-6.88-40.4-21.35-54.93-14.46-14.53-34.33-22.37-54.82-21.62ZM426.62,124.66c5.79-20.03,24.32-33.68,45.18-33.25,20.96-1.67,39.85,12.63,43.92,33.25h-89.1Z" />
          <path d="M635.84,58.16c-25.1,0-51.07,6.78-62.74,27.61l28.61,15.81c5.87-12.02,18.93-18.75,32.12-16.56,13.43,0,31.25,3.26,31.25,20.45,0,19.58-21.33,16.94-45.17,21.33-28.11,2.51-58.1,8.41-58.1,46.05,0,29.49,25.1,44.05,51.57,44.05,19.68.04,38.72-6.94,53.71-19.7h0l28.86,15.81v-111.06c.38-31.87-30.62-43.79-60.11-43.79ZM663.07,162.32c-.14,10.51-6.57,19.9-16.31,23.84-6.6,3.1-13.79,4.73-21.08,4.77-11.67,0-30.62-4.39-30.62-19.32,0-17.44,12.55-22.71,27.1-25.1,14.56-2.38,30.62-2.38,40.91-9.16v24.97Z" />
          <path d="M793.37,125.48c-21.33-4.77-34.89-7.78-34.89-18.45s8.53-17.32,25.47-17.32c13.23-.61,25.81,5.77,33.13,16.82l26.35-14.43c-13.43-19.33-35.99-30.22-59.48-28.74-35.01-.75-56.97,20.96-56.97,44.3,0,30.62,26.1,38.9,51.2,44.17,18.7,3.89,35.89,6.53,35.89,18.32s-9.41,19.95-27.23,19.95c-15.58.34-30.07-7.97-37.65-21.58l-28.49,14.81c13.27,22.92,38.46,36.27,64.88,34.38,42.16,0,59.86-27.23,59.86-47.68-.25-31.25-27.73-39.28-52.08-44.55Z" />
          <rect x="882.06" y="178.86" width="30.49" height="39.03" />
        </g>
      </svg>
    </div>

    <!-- AriaAnnouncer for screen readers -->
    <AriaAnnouncer ref="announcerRef" />

    <!-- Hidden audio element -->
    <audio
      ref="audioElement"
      :preload="config.autoPlay ? 'auto' : (config.preload || 'metadata')"
      :autoplay="config.autoPlay || undefined"
      :muted="config.muted || undefined"
      crossorigin="anonymous"
    />
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch, onMounted, onUnmounted, nextTick, type PropType, type Ref } from 'vue'
import { mdiMusic, mdiPlay, mdiAlertCircle, mdiChevronDown, mdiChevronUp, mdiDownload, mdiFileDocumentOutline, mdiFileImageOutline, mdiFileMusicOutline, mdiFileOutline } from '@mdi/js'
import type { AudioPlayerConfig, AudioPlayerCallbacks, WaveformData, AudioPlayerState, AudioChapter } from '../types'
import type { AudioPlaybackInfo, AudioAssetListItem, SubtitleTrack, LinkedFile } from '../../types/asset'
import type { PlayerError } from '../../types/base'
import type { CollectionItem } from '../../types/player'
import { useAudioPlayer } from '../composables/useAudioPlayer'
import { usePlaybackHeartbeat } from '../../common/composables/usePlaybackHeartbeat'
import { useColorExtraction } from '../composables/useColorExtraction'
import { useAudioChapters } from '../composables/useAudioChapters'
import { useAudioSubtitles } from '../composables/useAudioSubtitles'
import AudioToolbar from './AudioToolbar.vue'
import AudioWaveform from './AudioWaveform.vue'
import AudioProgressBar from './AudioProgressBar.vue'
import AudioPlaylistItem from './AudioPlaylistItem.vue'
import AriaAnnouncer from '../../common/components/AriaAnnouncer.vue'
import { formatTime, hexToRgb } from '../utils/audio-utils'
import { useAudioI18n } from '../i18n'

interface AudioPlayerConfigExtended extends AudioPlayerConfig {
  enableVolume?: boolean
  enablePlaybackSpeed?: boolean
  enableLoop?: boolean
  enableShuffle?: boolean
  enableAutoPlayNext?: boolean
  enableFullscreen?: boolean
}

const props = defineProps({
  config: {
    type: Object as PropType<AudioPlayerConfigExtended>,
    required: true,
  },
  playbackInfo: {
    type: Object as PropType<AudioPlaybackInfo>,
    default: undefined,
  },
  poster: {
    type: String,
    default: undefined,
  },
  showPlaylist: {
    type: Boolean,
    default: false,
  },
  brandingLogoUrl: {
    type: String,
    default: undefined,
  },
  showBranding: {
    type: Boolean,
    default: false,
  },
  theme: {
    type: String as PropType<'dark' | 'light' | 'colorful'>,
    default: 'colorful',
  },
  iconColor: {
    type: String,
    default: undefined,
  },
  /**
   * Whether the listener may move the playhead — the publication's `seeking`. `false` draws the
   * waveform and the bar still and refuses the keyboard's and the chapters' moves; the page that
   * framed the player keeps its `seek` command.
   */
  seeking: {
    type: Boolean,
    default: true,
  },
})

const emit = defineEmits<{
  itemChange: [index: number, item: CollectionItem<AudioAssetListItem>]
  play: []
  pause: []
  ended: []
  timeUpdate: [currentTime: number]
  durationChange: [duration: number]
  volumeChange: [volume: number, muted: boolean]
  playbackSpeedChange: [speed: number]
  waveformLoad: [waveform: WaveformData]
  fullscreenChange: [isFullscreen: boolean]
  playbackInfoNeeded: [assetId: string]
  error: [error: PlayerError]
}>()

// Refs
const containerRef: Ref<HTMLElement | null> = ref(null)
const audioElement: Ref<HTMLAudioElement | null> = ref(null)
const announcerRef = ref<InstanceType<typeof AriaAnnouncer> | null>(null)
const playlistItemsRef = ref<HTMLElement | null>(null)
const playlistItemRefs = ref<Map<number, HTMLElement>>(new Map())

// i18n
const { t } = useAudioI18n()

// Playlist state
const isPlaylistCollapsed = ref(false)

// Chapters state
const isChaptersCollapsed = ref(false)


// Initialize player
const player = useAudioPlayer({
  canSeek: () => props.seeking,
  config: props.config,
  initialPlaybackInfo: props.playbackInfo,
  coverArt: props.poster,
  callbacks: {
    onItemChange: (index: number, item: CollectionItem<AudioAssetListItem>) => {
      emit('itemChange', index, item)
    },
    onPlay: () => {
      emit('play')
    },
    onPause: () => {
      emit('pause')
    },
    onEnded: () => {
      emit('ended')
    },
    onTimeUpdate: (currentTime: number) => {
      emit('timeUpdate', currentTime)
    },
    onDurationChange: (duration: number) => {
      emit('durationChange', duration)
    },
    onVolumeChange: (volume: number, muted: boolean) => {
      emit('volumeChange', volume, muted)
    },
    onPlaybackSpeedChange: (speed: number) => {
      emit('playbackSpeedChange', speed)
    },
    onWaveformLoad: (waveform: WaveformData) => {
      emit('waveformLoad', waveform)
    },
    onFullscreenChange: (fs: boolean) => {
      emit('fullscreenChange', fs)
    },
    onError: (error: PlayerError) => {
      emit('error', error)
    },
  },
})

// Unified playback heartbeat — analytics (session) + resume store, for every
// viewer (anonymous or authenticated). Active whenever a playback token exists.
const heartbeatHasStarted = computed(() => player.currentTime.value > 0 || player.isPlaying.value)
usePlaybackHeartbeat({
  playbackToken: computed(() => props.config.playbackToken),
  assetId: computed(() => player.currentAsset.value?.uid),
  sessionId: computed(() => props.playbackInfo?.sessionId ?? undefined),
  currentTime: player.currentTime,
  duration: player.duration,
  hasStarted: heartbeatHasStarted,
  playState: player.playState,
})

// Computed
const currentTitle = computed(() => {
  const item = player.currentItem.value
  return item?.customTitle || item?.asset.name || t('player.status.unknownTrack')
})

const coverArtUrl = computed(() => {
  return props.poster || player.currentAsset.value?.thumbnailUrl
})

// Color extraction from cover art
const { theme: dynamicTheme } = useColorExtraction(coverArtUrl)

const accentColor = computed(() => {
  if (props.iconColor) return props.iconColor
  const rgb = dynamicTheme.value.dominantColorRgb
  return `rgb(${rgb[0]}, ${rgb[1]}, ${rgb[2]})`
})

const themeClass = computed(() => {
  if (props.theme === 'light') return 'audio-player--light'
  if (props.theme === 'dark') return 'audio-player--dark'
  return 'audio-player--colorful'
})

const dynamicStyles = computed(() => {
  if (props.iconColor) {
    // User-defined accent color overrides everything
    const rgb = hexToRgb(props.iconColor)
    const rgbStr = rgb ? `${rgb[0]}, ${rgb[1]}, ${rgb[2]}` : dynamicTheme.value.dominantColorRgb.join(', ')
    return {
      '--audio-accent-color': props.iconColor,
      '--audio-accent-rgb': rgbStr,
      '--audio-icon-color': props.iconColor,
    }
  }
  return {
    '--audio-accent-color': accentColor.value,
    '--audio-accent-rgb': dynamicTheme.value.dominantColorRgb.join(', '),
  }
})

// Chapters
const chapters = computed<AudioChapter[]>(() => props.config.chapters || [])

const chaptersComposable = useAudioChapters({
  chapters,
  currentTime: player.currentTime,
  duration: player.duration,
  onChapterChange: (_index, chapter) => {
    announcerRef.value?.announce(t('player.accessibility.chapter', { title: chapter.title }))
  },
})

const currentChapterTitle = computed(() => chaptersComposable.currentChapter.value?.title || '')

// Subtitles
const subtitleTracks = computed<SubtitleTrack[]>(() => props.config.subtitleTracks || [])
const subtitlesEnabled = ref(false)
const currentSubtitleTrackIndex = ref(0)

// Auto-enable if there's a default track
watch(subtitleTracks, (tracks) => {
  if (tracks.length > 0) {
    const defaultIdx = tracks.findIndex((t) => t.isDefault)
    if (defaultIdx >= 0) {
      currentSubtitleTrackIndex.value = defaultIdx
      subtitlesEnabled.value = true
    }
  }
}, { immediate: true })

const subtitlesComposable = useAudioSubtitles(
  audioElement,
  subtitleTracks,
  subtitlesEnabled,
  currentSubtitleTrackIndex,
)

const hasSubtitles = computed(() => subtitleTracks.value.length > 0)

// Downloads
const linkedFiles = computed<LinkedFile[]>(() => player.currentPlaybackInfo.value?.linkedFiles || [])
const sourceDownloadUrl = computed(() => player.sourceDownloadUrl.value)
const hasDownloads = computed(() => player.hasDownloads.value)
const isDownloadsCollapsed = ref(true)
const downloadsCount = computed(() => linkedFiles.value.length + (sourceDownloadUrl.value ? 1 : 0))

function linkedFileIcon(type: string): string {
  if (type === 'document') return mdiFileDocumentOutline
  if (type === 'image') return mdiFileImageOutline
  if (type === 'audio') return mdiFileMusicOutline
  return mdiFileOutline
}

// Auto-collapse playlist for large lists
watch(() => player.totalItems.value, (total) => {
  if (total > 5) {
    isPlaylistCollapsed.value = true
  }
}, { immediate: true })

// Auto-scroll to active playlist item
watch(() => player.currentIndex.value, (index) => {
  nextTick(() => {
    const itemEl = playlistItemRefs.value.get(index)
    if (itemEl) {
      itemEl.scrollIntoView({ behavior: 'smooth', block: 'nearest' })
    }
  })
})

function setPlaylistItemRef(index: number, el: HTMLElement | { $el: HTMLElement } | null): void {
  if (el) {
    playlistItemRefs.value.set(index, '$el' in el ? el.$el : el)
  } else {
    playlistItemRefs.value.delete(index)
  }
}

// Announce helpers
function announce(text: string): void {
  announcerRef.value?.announce(text)
}

// Event handlers with announcements
function handleTogglePlay(): void {
  player.togglePlay()
  announce(player.isPlaying.value ? t('player.accessibility.paused') : t('player.accessibility.playing', { title: currentTitle.value }))
}

function handlePrev(): void {
  player.prev()
  nextTick(() => {
    announce(t('player.accessibility.nowPlaying', { title: currentTitle.value }))
  })
}

function handleNext(): void {
  player.next()
  nextTick(() => {
    announce(t('player.accessibility.nowPlaying', { title: currentTitle.value }))
  })
}

function handleToggleMute(): void {
  player.toggleMute()
  announce(player.isMuted.value ? t('player.controls.unmute') : t('player.controls.mute'))
}

function handleVolumeChange(volume: number): void {
  player.setVolume(volume)
  announce(t('player.accessibility.volume', { value: Math.round(volume * 100) }))
}

function handleSpeedChange(speed: number): void {
  player.setPlaybackSpeed(speed)
  announce(t('player.accessibility.speed', { value: speed }))
}

// Tracks whether we successfully entered the *native* Fullscreen API, so an
// OS-driven exit (Escape, back gesture) can sync our state back.
let enteredNativeFullscreen = false

function handleToggleFullscreen(): void {
  const el = containerRef.value
  const goingFullscreen = !player.isFullscreen.value

  if (goingFullscreen) {
    // Try the native Fullscreen API first: on Android/desktop this escapes the
    // embed iframe and fills the whole screen. iOS Safari can't fullscreen a
    // non-<video> element, so the request may reject — the CSS fallback
    // (.audio-player--fullscreen, position: fixed) then takes over.
    const request =
      el?.requestFullscreen?.() ?? (el as unknown as { webkitRequestFullscreen?: () => Promise<void> })?.webkitRequestFullscreen?.()
    if (request && typeof request.then === 'function') {
      enteredNativeFullscreen = true
      request.catch(() => {
        enteredNativeFullscreen = false
      })
    }
  } else if (document.fullscreenElement) {
    document.exitFullscreen?.().catch(() => {})
    enteredNativeFullscreen = false
  }

  // Drives the CSS class + toolbar icon and emits fullscreenChange. This is the
  // single source of truth, so the player stays consistent whether the native
  // API succeeded or we fell back to CSS.
  player.toggleFullscreen()
}

// Keep our state in sync when the native fullscreen is exited outside our
// control (Escape key, system back gesture).
function handleNativeFullscreenChange(): void {
  if (!document.fullscreenElement && enteredNativeFullscreen && player.isFullscreen.value) {
    enteredNativeFullscreen = false
    player.isFullscreen.value = false
  }
}

function handleSeek(time: number): void {
  player.seek(time)
}

function handleToggleSubtitles(): void {
  subtitlesEnabled.value = !subtitlesEnabled.value
  announce(subtitlesEnabled.value ? t('player.controls.subtitles') : t('player.controls.subtitlesOff'))
}

function handleSubtitleTrackChange(index: number): void {
  currentSubtitleTrackIndex.value = index
  subtitlesEnabled.value = true
}

function handleRetry(): void {
  player.clearError()
  const currentAsset = player.currentAsset.value
  if (currentAsset) {
    player.requestPlaybackInfo(currentAsset.uid)
  }
}

function handleKeyDown(event: KeyboardEvent): void {
  switch (event.key) {
    case ' ':
    case 'k':
      event.preventDefault()
      handleTogglePlay()
      break
    case 'ArrowLeft':
      // A key that moves the playhead is the page's while the listener may not seek.
      if (!player.canSeek.value) break
      event.preventDefault()
      if (event.shiftKey) {
        player.seek(player.currentTime.value - 30)
      } else {
        player.seek(player.currentTime.value - 10)
      }
      break
    case 'ArrowRight':
      if (!player.canSeek.value) break
      event.preventDefault()
      if (event.shiftKey) {
        player.seek(player.currentTime.value + 30)
      } else {
        player.seek(player.currentTime.value + 10)
      }
      break
    case 'ArrowUp':
      event.preventDefault()
      player.setVolume(Math.min(1, player.volume.value + 0.1))
      break
    case 'ArrowDown':
      event.preventDefault()
      player.setVolume(Math.max(0, player.volume.value - 0.1))
      break
    case 'm':
    case 'M':
      event.preventDefault()
      handleToggleMute()
      break
    case 'n':
    case 'N':
      event.preventDefault()
      if (event.shiftKey) {
        handlePrev()
      } else {
        handleNext()
      }
      break
    case 'p':
    case 'P':
      event.preventDefault()
      handlePrev()
      break
    case 'l':
    case 'L':
      event.preventDefault()
      player.toggleLoop()
      break
    case 's':
    case 'S':
      event.preventDefault()
      player.toggleShuffle()
      break
    case 'a':
    case 'A':
      event.preventDefault()
      player.toggleAutoPlayNext()
      break
    case 'f':
    case 'F':
      event.preventDefault()
      handleToggleFullscreen()
      break
    case 'Home':
      event.preventDefault()
      player.first()
      break
    case 'End':
      event.preventDefault()
      player.last()
      break
  }
}

// Announce track changes
watch(() => player.currentIndex.value, () => {
  nextTick(() => {
    announce(t('player.accessibility.nowPlaying', { title: currentTitle.value }))
  })
})

// Watch for external playback info
watch(
  () => props.playbackInfo,
  (newInfo) => {
    if (newInfo) {
      player.setPlaybackInfo(newInfo.uid, newInfo)
    }
  },
)

// Connect audio element ref to player
watch(audioElement, (el) => {
  player.audioRef.value = el
})

// Initialize
onMounted(() => {
  player.audioRef.value = audioElement.value
  // No `focus()` here: the player is mounted inside somebody else's page, and focusing on load
  // scrolls that page to the iframe and takes its keyboard. The container is `tabindex="0"`, so
  // a click or a Tab into it gives its key handler focus — as the video player has always done.
  document.addEventListener('fullscreenchange', handleNativeFullscreenChange)
})

// Cleanup
onUnmounted(() => {
  document.removeEventListener('fullscreenchange', handleNativeFullscreenChange)
  player.destroy()
})

// Expose player methods for external control
defineExpose({
  player,
  play: player.play,
  pause: player.pause,
  togglePlay: player.togglePlay,
  // The page that framed the player owns this one (`docs/plans/embed-sdk.md` ES6): it reaches the
  // playhead whatever `seeking` says.
  seek: player.seekTo,
  next: player.next,
  prev: player.prev,
  setVolume: player.setVolume,
  setPlaybackSpeed: player.setPlaybackSpeed,
  goToIndex: player.goToIndex,
  setPlaybackInfo: player.setPlaybackInfo,
  chapters: chaptersComposable,
})
</script>

<style lang="scss" scoped>
@use '@/styles/design-tokens' as *;

.audio-player {
  position: relative;
  display: flex;
  flex-direction: column;
  background: rgb(var(--v-theme-surface));
  border-radius: $radius-lg;
  overflow: hidden;
  outline: none;

  @include focus-visible-only;

  // Theme variants
  &--light {
    --v-theme-on-surface: 26, 26, 26;
    --v-theme-surface: 255, 255, 255;
    --v-border-color: 0, 0, 0;
    --v-border-opacity: 0.12;
    background: #FFFFFF;
    color: #1a1a1a;
  }

  &--dark {
    --v-theme-on-surface: 255, 255, 255;
    --v-theme-surface: 26, 26, 26;
    --v-border-color: 255, 255, 255;
    --v-border-opacity: 0.12;
    background: #1a1a1a;
    color: #FFFFFF;
  }

  &--colorful {
    background: rgb(var(--v-theme-surface));
  }

  &--fullscreen {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    z-index: 9999;
    border-radius: 0;
  }

  // Background blur
  &__bg {
    position: absolute;
    inset: 0;
    overflow: hidden;
    pointer-events: none;
  }

  &__bg-image {
    width: 100%;
    height: 100%;
    object-fit: cover;
    filter: blur(60px) saturate(1.8) brightness(0.9);
    opacity: 0.4;
    transform: scale(1.3);
  }

  &__bg-overlay {
    position: absolute;
    inset: 0;
    background: linear-gradient(
      180deg,
      rgba(var(--v-theme-surface), 0.45) 0%,
      rgba(var(--v-theme-surface), 0.7) 40%,
      rgba(var(--v-theme-surface), 0.92) 100%
    );
  }

  // Mobile: vertical layout (default)
  &__content {
    position: relative;
    display: flex;
    flex-direction: column;
    align-items: center;
    padding: $space-xl;
    gap: $space-lg;
    flex: 1;
    z-index: 1;
  }

  &__cover {
    position: relative;
    width: 120px;
    height: 120px;
    border-radius: $radius-lg;
    overflow: hidden;
    box-shadow:
      0 8px 32px rgba(0, 0, 0, 0.2),
      0 2px 8px rgba(0, 0, 0, 0.1);
    flex-shrink: 0;
    @include transition-normal(transform);

    .audio-player--playing & {
      transform: scale(1.03);
      box-shadow:
        0 12px 40px rgba(var(--audio-accent-rgb, 0, 0, 0), 0.3),
        0 4px 12px rgba(0, 0, 0, 0.15);
    }

    @include reduced-motion {
      transform: none !important;
    }
  }

  &__cover-image {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  &__cover-placeholder {
    width: 100%;
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    background: linear-gradient(
      135deg,
      rgba(var(--v-theme-primary), 0.2),
      rgba(var(--v-theme-secondary), 0.2)
    );
  }

  &__cover-play {
    position: absolute;
    inset: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    background: rgba(0, 0, 0, 0.35);
    border: none;
    cursor: pointer;
    opacity: 0;
    @include transition-fast(opacity);

    .audio-player__cover:hover & {
      opacity: 1;
    }

    @include focus-visible-only;

    @include reduced-motion {
      opacity: 1;
    }
  }

  &__main {
    position: relative;
    display: flex;
    flex-direction: column;
    flex: 1;
    min-width: 0;
    gap: $space-md;
    width: 100%;
    align-items: center;
    z-index: 1;
  }

  &__info {
    text-align: center;
    max-width: 100%;
    min-width: 0;
  }

  &__title {
    font-size: $text-lg;
    font-weight: $font-semibold;
    color: rgba(var(--v-theme-on-surface), $opacity-primary);
    margin: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  &__chapter-label {
    display: block;
    margin-top: $space-xs;
    font-size: $text-sm;
    font-weight: $font-medium;
    color: rgba(var(--v-theme-on-surface), $opacity-secondary);
  }

  &__waveform {
    width: 100%;
    max-width: 500px;
  }

  &__subtitle {
    position: relative;
    width: 100%;
    max-width: 500px;
    height: 3em;
    margin: 0;
    border-radius: $radius-sm;
    background: rgba(var(--v-theme-on-surface), 0.06);
  }

  &__subtitle-text {
    position: absolute;
    inset: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: $space-xs $space-md;
    font-size: $text-md;
    font-weight: $font-medium;
    line-height: $leading-relaxed;
    color: rgba(var(--v-theme-on-surface), $opacity-primary);
    text-align: center;
  }

  &__error {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: $space-md;
    padding: $space-lg;
    text-align: center;
  }

  &__error-text {
    font-size: $text-md;
    color: rgba(var(--v-theme-on-surface), 0.75);
  }

  // Desktop: horizontal compact layout
  @media (min-width: 600px) {
    &__content {
      flex-direction: row;
      align-items: center;
      padding: $space-lg $space-xl;
      gap: $space-xl;
    }

    &__cover {
      width: 100px;
      height: 100px;
      border-radius: $radius-md;
    }

    &__main {
      align-items: flex-start;
      gap: $space-sm;
    }

    &__info {
      text-align: left;
    }

    &__title {
      font-size: 15px;
    }

    &__waveform {
      max-width: none;
    }

    &__subtitle {
      max-width: none;
    }
  }

  // Playlist section
  &__playlist {
    position: relative;
    border-top: 1px solid rgba(var(--v-border-color), var(--v-border-opacity));
    max-height: 300px;
    display: flex;
    flex-direction: column;
    z-index: 1;
  }

  &__playlist-header {
    display: flex;
    align-items: center;
    gap: $space-sm;
    padding: $space-md $space-lg;
    background: rgba(var(--v-theme-on-surface), 0.02);
    border: none;
    cursor: pointer;
    width: 100%;
    text-align: left;
    @include transition-fast(background-color);

    @include focus-visible-only;

    &:hover {
      background: rgba(var(--v-theme-on-surface), 0.05);
    }
  }

  &__playlist-toggle {
    display: flex;
    align-items: center;
    color: rgba(var(--v-theme-on-surface), $opacity-secondary);
  }

  &__playlist-title {
    flex: 1;
    font-size: $text-md;
    font-weight: $font-semibold;
    color: rgba(var(--v-theme-on-surface), $opacity-primary);
  }

  &__playlist-count {
    font-size: $text-sm;
    color: rgba(var(--v-theme-on-surface), $opacity-tertiary + 0.1);
    font-variant-numeric: tabular-nums;
  }

  &__playlist-items {
    flex: 1;
    overflow-y: auto;
    padding: $space-sm;
  }

  // Chapters section
  &__chapters {
    position: relative;
    border-top: 1px solid rgba(var(--v-border-color), var(--v-border-opacity));
    max-height: 300px;
    display: flex;
    flex-direction: column;
    z-index: 1;
  }

  &__chapters-header {
    display: flex;
    align-items: center;
    gap: $space-sm;
    padding: $space-md $space-lg;
    background: rgba(var(--v-theme-on-surface), 0.02);
    border: none;
    cursor: pointer;
    width: 100%;
    text-align: left;
    @include transition-fast(background-color);

    @include focus-visible-only;

    &:hover {
      background: rgba(var(--v-theme-on-surface), 0.05);
    }
  }

  &__chapters-toggle {
    display: flex;
    align-items: center;
    color: rgba(var(--v-theme-on-surface), $opacity-secondary);
  }

  &__chapters-title {
    flex: 1;
    font-size: $text-md;
    font-weight: $font-semibold;
    color: rgba(var(--v-theme-on-surface), $opacity-primary);
  }

  &__chapters-count {
    font-size: $text-sm;
    color: rgba(var(--v-theme-on-surface), $opacity-tertiary + 0.1);
    font-variant-numeric: tabular-nums;
  }

  &__chapters-list {
    flex: 1;
    overflow-y: auto;
    list-style: none;
    margin: 0;
    padding: $space-xs $space-sm;
  }

  &__chapters-item {
    display: flex;
    align-items: center;
    gap: $space-md;
    padding: $space-sm $space-md;
    border-radius: $radius-sm;
    cursor: pointer;
    @include transition-fast(background-color);

    &:hover {
      background: rgba(var(--v-theme-on-surface), 0.05);
    }

    // A table of contents, not a way in: the listener may not move the playhead (`seeking`).
    &--still {
      cursor: default;
    }

    &--still:not(.audio-player__chapters-item--active):hover {
      background: none;
    }

    &--active {
      background: rgba(var(--audio-accent-rgb, var(--v-theme-primary)), 0.1);

      .audio-player__chapters-name {
        font-weight: $font-semibold;
      }
    }
  }

  &__chapters-time {
    font-size: $text-sm;
    font-weight: $font-medium;
    font-variant-numeric: tabular-nums;
    color: rgba(var(--v-theme-on-surface), $opacity-secondary);
    min-width: 44px;
    flex-shrink: 0;
  }

  &__chapters-name {
    font-size: $text-sm;
    color: rgba(var(--v-theme-on-surface), $opacity-primary);
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  // Downloads section
  &__downloads {
    position: relative;
    border-top: 1px solid rgba(var(--v-border-color), var(--v-border-opacity));
    max-height: 300px;
    display: flex;
    flex-direction: column;
    z-index: 1;
  }

  &__downloads-header {
    display: flex;
    align-items: center;
    gap: $space-sm;
    padding: $space-md $space-lg;
    background: rgba(var(--v-theme-on-surface), 0.02);
    border: none;
    cursor: pointer;
    width: 100%;
    text-align: left;
    @include transition-fast(background-color);

    @include focus-visible-only;

    &:hover {
      background: rgba(var(--v-theme-on-surface), 0.05);
    }
  }

  &__downloads-toggle {
    display: flex;
    align-items: center;
    color: rgba(var(--v-theme-on-surface), $opacity-secondary);
  }

  &__downloads-title {
    flex: 1;
    font-size: $text-md;
    font-weight: $font-semibold;
    color: rgba(var(--v-theme-on-surface), $opacity-primary);
  }

  &__downloads-count {
    font-size: $text-sm;
    color: rgba(var(--v-theme-on-surface), $opacity-tertiary + 0.1);
    font-variant-numeric: tabular-nums;
  }

  &__downloads-list {
    flex: 1;
    overflow-y: auto;
    padding: $space-xs $space-sm;
  }

  &__downloads-item {
    display: flex;
    align-items: center;
    gap: $space-md;
    padding: $space-sm $space-md;
    border-radius: $radius-sm;
    cursor: pointer;
    text-decoration: none;
    color: rgba(var(--v-theme-on-surface), $opacity-primary);
    @include transition-fast(background-color);

    &:hover {
      background: rgba(var(--v-theme-on-surface), 0.05);
    }

    &--disabled {
      opacity: $opacity-disabled;
      cursor: default;
      pointer-events: none;
    }
  }

  &__downloads-name {
    flex: 1;
    min-width: 0;
    font-size: $text-sm;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  &__downloads-action {
    flex-shrink: 0;
    opacity: $opacity-tertiary;
    @include transition-fast(opacity);

    .audio-player__downloads-item:hover & {
      opacity: $opacity-secondary;
    }
  }

  // Branding
  &__branding {
    position: relative;
    display: flex;
    justify-content: flex-end;
    padding: 0 $space-lg $space-sm;
    z-index: 1;
    pointer-events: none;
  }

  &__branding-logo {
    color: rgb(var(--v-theme-on-surface));
  }

  &__branding-custom-logo {
    max-height: 24px;
    max-width: 120px;
    object-fit: contain;
  }
}

// Transitions
.audio-cover-fade-enter-active,
.audio-cover-fade-leave-active {
  transition: opacity $duration-normal $ease-default;
}

.audio-cover-fade-enter-from,
.audio-cover-fade-leave-to {
  opacity: 0;
}

.audio-title-fade-enter-active,
.audio-title-fade-leave-active {
  transition: opacity $duration-fast $ease-default, transform $duration-fast $ease-default;
}

.audio-title-fade-enter-from {
  opacity: 0;
  transform: translateY(4px);
}

.audio-title-fade-leave-to {
  opacity: 0;
  transform: translateY(-4px);
}

.audio-subtitle-fade-enter-active,
.audio-subtitle-fade-leave-active {
  transition: opacity $duration-fast $ease-default;
}

.audio-subtitle-fade-enter-from,
.audio-subtitle-fade-leave-to {
  opacity: 0;
}

.audio-subtitle-fade-enter-to,
.audio-subtitle-fade-leave-from {
  opacity: 1;
}

.audio-playlist-collapse-enter-active,
.audio-playlist-collapse-leave-active {
  transition: max-height $duration-normal $ease-default, opacity $duration-normal $ease-default;
  overflow: hidden;
}

.audio-playlist-collapse-enter-from,
.audio-playlist-collapse-leave-to {
  max-height: 0;
  opacity: 0;
}

.audio-playlist-collapse-enter-to,
.audio-playlist-collapse-leave-from {
  max-height: 300px;
  opacity: 1;
}

@include reduced-motion {
  .audio-cover-fade-enter-active,
  .audio-cover-fade-leave-active,
  .audio-title-fade-enter-active,
  .audio-title-fade-leave-active,
  .audio-subtitle-fade-enter-active,
  .audio-subtitle-fade-leave-active,
  .audio-playlist-collapse-enter-active,
  .audio-playlist-collapse-leave-active {
    transition: none;
  }
}
</style>
